<template>
    <div class="table-outer-wrapper" :class="{ 'no-top-filters': props.hideTopFilters }">
        <div v-if="!props.hideTopFilters" class="header-row" ref="headerSlot">
            <slot name="header">
                <h2>{{ props.header ?? '' }}</h2>
            </slot>
            <div class="button-row" v-if="rows">
                <conditional-teleport :to="props.optionsTeleport || null">
                    <prime-button v-if="!props.preventDownload" v-tooltip.left="'Download table'" text @click="downloadExcel">
                        <font-awesome-icon :icon="['fas', 'download']" />
                    </prime-button>
                </conditional-teleport>
            </div>
        </div>
        <generic-table
            v-if="rows"
            :data="genericTableData"
            :small-header="props.smallHeader"
            :small-cells="props.smallCells"
            hide-top-filters
            cannot-edit
            row-click-mode="disabled"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { ISeriesList } from '@/interfaces/powerMarkets/series/ISeriesList.ts';
import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { ITableSeries } from '@/interfaces/powerMarkets/table/ITableSeries.ts';
import { cloneDeep } from '@/utils/global.ts';
import { SeriesDataArray } from '@/interfaces/powerMarkets/series/ISeriesFrontEnd.ts';
import dayjs from 'dayjs';
import ConditionalTeleport from '@/components/layout/ConditionalTeleport.vue';
import PrimeButton from 'primevue/button';
import { getPeriodFromTime } from '@/utils/periodUtils.ts';
import { exportJsonToExcel, exportJsonToCSV } from '@/utils/excelUtils.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { buildGenericTableData } from '@/helpers/table/genericTableData.ts';
import { createDefaultFilterProperty } from '@/helpers/table/tableFilterColumnsFromData.ts';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IGenericTableRowFormatting } from '@/interfaces/powerMarkets/table/IGenericTableRowFormatting.ts';
import GenericTable from './GenericTable.vue';

const props = defineProps<{
    chartConfig: IModuleChartConfig;
    chartData: ISeriesList;
    hiddenHeadings: Array<string>;
    preventDownload?: boolean;
    smallHeader?: boolean;
    smallCells?: boolean;
    hideTopFilters?: boolean;
    optionsTeleport?: HTMLElement | null;
    header?: string;
    excelFilename?: string;
}>();

const hasTimeAxis = computed(() => {
    if ('Time' in props.chartConfig.data.xAxisNameToChartId) return true;
    if ('Time' in props.chartConfig.data.yAxisNameToChartId) return true;
    if (props.chartConfig.info.extraChartInfo?.showTime) return true;
    return false;
});
const chartSeries = ref<Record<string, ITableSeries> | null>(null);
const userSettingsStore = useUserSettingsStore();

interface IChartTableCell {
    displayString: string;
    rawValue: undefined | number | Array<number>;
}

const rows = ref<Record<string, Array<IChartTableCell>> | null>(null);

const genericTableData = computed<IGenericTableData>(() => {
    const columns = [
        ...(hasTimeAxis.value ? [{ id: 'period', title: 'Period' }, { id: 'time', title: 'Time' }] : []),
        ...Object.values(chartSeries.value ?? {}).map(series => ({ id: series.id, title: series.name })),
    ];
    const tableRows = Object.values(rows.value ?? {}).map(buildGenericTableRow);

    return buildGenericTableData(columns, tableRows);
});

function buildGenericTableRow (row: Array<IChartTableCell>): Array<IGenericTableRowFormatting> {
    return row.map((cell, index): IGenericTableRowFormatting => {
        const isTime = hasTimeAxis.value && index === 1;
        const rawValue = isTime && typeof cell.rawValue === 'number' ? cell.rawValue / 1000 : cell.rawValue;
        return {
            ...createDefaultFilterProperty(isTime ? 'date' : 'number'),
            values: Array.isArray(rawValue) ? rawValue : [rawValue ?? null],
            displayValues: [cell.displayString],
        };
    });
}

function buildChartSeries (): void {
    const configWithData: Record<string, ITableSeries> = {};

    props.chartConfig.data.config.forEach((configNotCloned) => {
        if (props.hiddenHeadings.includes(configNotCloned.name)) return;

        const seriesData = cloneDeep(configNotCloned);

        if (('data' in seriesData) && seriesData.id && props.chartData.seriesFrontEnd[seriesData.id]) {
            let newSeriesData: SeriesDataArray = [];
            Object.values(props.chartData!.seriesFrontEnd[seriesData.id]).forEach((realtimeSeriesOption) => {
                if (realtimeSeriesOption === null) return;
                newSeriesData = newSeriesData.concat(realtimeSeriesOption);
            });
            seriesData.data = newSeriesData;
        }

        configWithData[seriesData.id] = seriesData;
    });
    chartSeries.value = configWithData;
}

function roundToTwo (num: number): number {
    return +(`${Math.round(Number(`${num}e+2`))}e-2`);
}

function buildTableData (): void {
    if (!chartSeries.value) return;
    const tableRows: Record<string, Array<IChartTableCell>> = {};
    const dataByTimeStamp: Record<string, Record<string, undefined | number | Array<number>>> = {};
    const minutesSorted: Array<number> = [];

    Object.values(chartSeries.value).forEach((seriesData) => {
        if (!Array.isArray(seriesData.data)) return;
        seriesData.data.forEach((datapoint) => {
            let minute: number | undefined;
            let value: number | Array<number> | undefined;
            if (Array.isArray(datapoint)) {
                minute = Number(datapoint[0]);
                value = Number(datapoint[1]);
            }
            if (minute !== undefined) {
                if (!(minute in dataByTimeStamp)) {
                    dataByTimeStamp[minute] = {};
                    minutesSorted.push(minute);
                }
                dataByTimeStamp[minute][seriesData.id] = value;
            }
        });
    });

    minutesSorted.sort((a, b) => a - b);

    minutesSorted.forEach((minute) => {
        const minutesData = dataByTimeStamp[minute];
        if (hasTimeAxis.value) {
            const period = getPeriodFromTime(dayjs.unix(minute / 1000));
            tableRows[minute] = [{
                displayString: String(period),
                rawValue: period,
            }, {
                displayString: dayjs.unix(minute / 1000).format('DD/MM/YYYY, HH:mm'),
                rawValue: minute,
            }];
        } else {
            tableRows[minute] = [];
        }
        Object.entries(chartSeries.value!).forEach(([seriesId, seriesData]) => {
            let displayString = '-';
            if (seriesId in minutesData) {
                const dataPoint = minutesData[seriesId];
                if (dataPoint && Array.isArray(dataPoint)) {
                    displayString = `${seriesData.yPrefix + roundToTwo(dataPoint[0]) + seriesData.ySuffix}, ${
                        seriesData.zPrefix}${roundToTwo(dataPoint[1])}${seriesData.zSuffix}`;
                } else if (dataPoint !== undefined && dataPoint !== null) {
                    displayString = seriesData.yPrefix + roundToTwo(dataPoint) + seriesData.ySuffix;
                }
            }
            tableRows[minute].push({ displayString, rawValue: minutesData[seriesId] });
        });
    });

    rows.value = tableRows;
}

function getExcelData (): Array<Record<string, number | string | Array<number>>> {
    const excel: Array<Record<string, number | string | Array<number>>> = [];
    if (!rows.value || !chartSeries.value) return excel;

    const headersByIndex: Record<number, string> = {};

    Object.values(chartSeries.value).forEach((series, index) => {
        if (!series.name) return;
        headersByIndex[index] = series.name;
    });

    Object.values(rows.value).forEach((row) => {
        const excelRow: Record<string, number | string | Array<number>> = {
            Date: dayjs.unix(Number(row[1].rawValue) / 1000).format('DD/MM/YYYY HH:mm'),
            Period: row[0].displayString,
        };

        row.slice(2).forEach((cell, index) => {
            if (!headersByIndex[index]) return;
            excelRow[headersByIndex[index]] = cell.rawValue ?? '';
        });
        excel.push(excelRow);
    });

    return excel;
}

function downloadExcel (): void {
    const title = `${props.excelFilename || props.chartConfig.info.name || 'Table download'} at ${dayjs().format()}`;
    const excel = getExcelData();
    if (userSettingsStore.userSettings.otherSettings.data.excelAsCsv) {
        exportJsonToCSV(excel, title);
    } else {
        exportJsonToExcel(excel, title);
    }
}

function getReportCsvRows (): Array<Array<string | number | null | undefined>> {
    if (!rows.value || !chartSeries.value) return [];

    const headers: Array<string> = [];
    if (hasTimeAxis.value) {
        headers.push('Period', 'Time');
    }
    headers.push(...Object.values(chartSeries.value).map(series => series.name ?? ''));

    return [headers, ...Object.values(rows.value).map(row => row.map(cell => cell.displayString))];
}

function initialise (): void {
    buildChartSeries();
    buildTableData();
}

initialise();

watch([
    (): ISeriesList => props.chartData,
    (): IModuleChartConfig => props.chartConfig,
    (): Array<string> => props.hiddenHeadings,
], () => {
    initialise();
});

defineExpose({
    downloadExcel,
    getReportCsvRows,
});

</script>

<style lang="scss" scoped>
.table-outer-wrapper {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    max-height: 100%;
    overflow: hidden;

    &.no-top-filters {
        grid-template-rows: minmax(0, 1fr);
    }

    .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--smallSpacing);
    }
}
</style>
